import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useScreen } from 'common/hooks/useScreen';
import { useMsal } from '@azure/msal-react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Logout from '@mui/icons-material/Logout';
import { UserDetail, UserProfileImage } from 'common/components';
import { getUserAgentBrowser, getUserAgentOS, isLocalStorageKeyExistDefined } from 'TAP/utils/commonMethods';
import { ListItemIcon } from '@mui/material';
import { TransferUniversalDashboardData } from 'TAP/redux/actions';
import { DASHBOARD_DATA_ACCEPTABLE_STATUSES, DELEGATEPROFILE, SELFPROFILE, SETDELEGATE } from 'common/global/constants';
import { userMenuSelectedAction } from 'common/redux/actions/appActions';
import { RedTheme } from 'common/global';
import { DelegateSwitch } from 'TAP/redux/actions/delegateUserAction';

/**
 * UserMenu.
 * Use UserProfileImage Component
 * This component using Box components for layour design
 * This component using Typography for text styling
 * This component using Tooltip Component to show tooltip on hover/click
 * This component using Menu Component to navigate user on specific screen
 * This component using MenuItem Component to show menu items
 * This component using IconButton Component to show Icon type button
 * @returns {JSX.Element}
 */
const defaultImageHeight = '40px';
const defaultImageHeightForMobile = '32px';

export const UserMenu = () => {
  let { instance } = useMsal();
  const dispatch = useDispatch();
  const { isMobile } = useScreen();
  const theme = RedTheme
  const logInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
  const loggedInUserEmail = logInUserDetails?.user_email ?? null;
  const isUniversalDashboardDataTransfered = useSelector(
    (state) => state?.universalDashboardReducer?.isUniversalDashboardDataTransfered,
  );
  const delegateSwitchRes = useSelector((state) => state?.delegateUserRd?.delegateSwitchRes);
  const isLoadingDelegateSwitch = useSelector((state) => state?.delegateUserRd?.isLoadingDelegateSwitchRes);
  
  const [logoutDashboardEventRegistered, setLogoutDashboardEventRegistered] = useState(false);
  const [disableDelegateMenu, setDisableDelegateMenu] = useState(false);
  const [disableManagerMenu, setDisableManagerMenu] = useState(false);
  const [toggleDisableDelegateSelf, setToggleDisableDelegateSelf] = useState(true);

  useEffect(() => {
    if (
      logoutDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(isUniversalDashboardDataTransfered)
    ) {
      setLogoutDashboardEventRegistered(false);
      handleLogoutUser();
    }
  }, [logoutDashboardEventRegistered, isUniversalDashboardDataTransfered]);

  useEffect(() => {
    menuOptionDisable();
  }, [])

  useEffect(() => {
    setDisableDelegateMenu(logInUserDetails?.is_DelegateEnable === 'N');
  }, [logInUserDetails?.is_DelegateEnable])

  useEffect(() => {
    menuOptionDisable();
    if (!isLoadingDelegateSwitch && delegateSwitchRes && Object.hasOwn(delegateSwitchRes,'isDelegateSwitch')) {
      delegateSwicthMenuOptionDisable(delegateSwitchRes);
    } else if (isLocalStorageKeyExistDefined('delegateProfileSwitched') && localStorage.getItem('delegateProfileSwitched') !== '[object Object]') {
        const delegateObj = JSON.parse(localStorage.getItem('delegateProfileSwitched'));
        delegateSwicthMenuOptionDisable(delegateObj);
    } else {
      setToggleDisableDelegateSelf(false);
    }
  }, [delegateSwitchRes, isLoadingDelegateSwitch])

  /**
   * Handle Logout User.
   */
  const handleLogoutUser = () => {
    localStorage.clear();
    instance.logout();
  };
  const [pageExitDashboardEventRegistered, setPageExitDashboardEventRegistered] = useState(false);
  const recordPageExitEvent = () => {
    dispatch(DelegateSwitch({loggedInUserId: '', loggedInUserEmail: UserDetail().userEmailId, type: 'self'}));
    const currLogInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
    setPageExitDashboardEventRegistered(true);
    dispatch(
      TransferUniversalDashboardData({
        apiType: 'page_exit',
        apiSubType: '',
        userEmailId: currLogInUserDetails?.user_email,
        pageTitle: currLogInUserDetails?.pageTitle ?? null,
        pageSubTitle: currLogInUserDetails?.pageSubTitle ?? null,
        pageURL: window.location.href,
        appName: process.env.REACT_APP_NAME,
        targetUrl: 'pagevisit/entry',
        ipAddress: null,
        idSession: currLogInUserDetails?.idSession ?? null,
        browser: getUserAgentBrowser(),
        os: getUserAgentOS(),
        visitStartTime: currLogInUserDetails?.visitStartTime ?? null,
        visitEndTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
        id: currLogInUserDetails?.id ?? null,
      }),
    );
  };
  useEffect(() => {
    if (
      pageExitDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(isUniversalDashboardDataTransfered)
    ) {
      setPageExitDashboardEventRegistered(false);
      recordLogoutEvent();
    }
  }, [pageExitDashboardEventRegistered, isUniversalDashboardDataTransfered]);
  const recordLogoutEvent = () => {
    setLogoutDashboardEventRegistered(true);
    dispatch(
      TransferUniversalDashboardData({
        apiType: 'user_session_logout_usermenu',
        apiSubType: '',
        userEmailId: loggedInUserEmail,
        appName: process.env.REACT_APP_NAME,
        targetUrl: 'session',
        startDateTime: logInUserDetails?.loggedInTime,
        endDateTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
        ipAddress: null,
        idSession: logInUserDetails?.idSession ?? null,
        browser: getUserAgentBrowser(),
        os: getUserAgentOS(),
      }),
    );
  };

  const userMenuClick = (_event) => {
    dispatch(userMenuSelectedAction(_event.target.innerText));
  };

  /**Delegate menu Enable/Disable from profile API*/
  const menuOptionDisable = () => {
    if (logInUserDetails && logInUserDetails?.is_DelegateEnable === 'N') {
      setDisableDelegateMenu(true);
    } if (logInUserDetails && logInUserDetails?.is_DelegateEnable === 'Y') {
      setDisableDelegateMenu(false);
    } if (logInUserDetails && logInUserDetails?.is_ManagerEnable === 'Y') {
      setDisableManagerMenu(false);
    } if (logInUserDetails && logInUserDetails?.is_ManagerEnable === 'N') {
      setDisableManagerMenu(true);
    }
  }

  /**Delegate menu Enable/disable logic from either store or local storage if we switch */
  const delegateSwicthMenuOptionDisable = (delegateObj) => {
    if (delegateObj?.isDelegateSwitch) {
        setToggleDisableDelegateSelf(true);
        setDisableDelegateMenu(false);
      } else if (!delegateObj?.isDelegateSwitch) {
        setToggleDisableDelegateSelf(false);
        setDisableDelegateMenu(false);
      } if (delegateObj?.IsManagerEnable === 'N') {
        setDisableManagerMenu(true);
      } else if (delegateObj?.IsManagerEnable === 'Y') {
        setDisableManagerMenu(false);
      }
  }

  const [anchorElUser, setAnchorElUser] = useState(null);

  /**
   * Handle Open user Menu.
   */
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  /**
   * Handle Close User Menu.
   */
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <>
      <Box
        sx={{
          flexGrow: 0,
          display: 'flex',
        }}
      >
        <Typography
          variant='h6'
          noWrap
          component='a'
          sx={{
            mr: 1,
            width: '100%',
            height: '18px',
            fontFamily: '"JohnsonText-Light"',
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '16px',
            color: '#212121',
            textDecoration: 'none',
            alignSelf: 'center',
            textAlign: 'end',
          }}
        >
          Hi {UserDetail()?.userFirstName} {UserDetail()?.userLastName}
        </Typography>
        <Tooltip>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <UserProfileImage height={isMobile ? defaultImageHeightForMobile : defaultImageHeight }></UserProfileImage>
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorElUser}
          id='account-menu'
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          onClick={handleCloseUserMenu}
          disableScrollLock={true}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Delegate menu */}
          {process.env.REACT_APP_FEATURE_TOGGLE_ROLEPROFILE &&
            process.env.REACT_APP_FEATURE_TOGGLE_ROLEPROFILE !== 'OFF' && (
              <Fragment>
                <MenuItem disabled={disableDelegateMenu || toggleDisableDelegateSelf} sx={{ backgroundColor: disableDelegateMenu ? theme.palette.stack.orange : `transparent !important` }} onClick={userMenuClick}>
                  {DELEGATEPROFILE}
                </MenuItem>
                <MenuItem disabled={disableDelegateMenu || !toggleDisableDelegateSelf} sx={{ backgroundColor: disableDelegateMenu ? theme.palette.stack.orange : `transparent !important` }} onClick={userMenuClick}>
                  {SELFPROFILE}
                </MenuItem>
                <MenuItem disabled={disableManagerMenu} onClick={userMenuClick}>
                  {SETDELEGATE}
                </MenuItem>
              </Fragment>
          )}
          {/* Delegate menu */}
          <MenuItem onClick={recordPageExitEvent}>
            <ListItemIcon>
              <Logout fontSize='small' />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
};